import { useMemo } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Loader2, Check, Star, Zap, Users, MessageSquare, BarChart3, Shield, Crown, CreditCard, Building2 } from "lucide-react"

interface Plan {
    id: string
    product_name: string
    product_description: string
    price: number
    currency: string
    interval: 'month' | 'year'
    features: (string | { name: string; })[]
    structured_features?: {
        max_brands?: string
        monthly_credits?: string
        features?: string
        [key: string]: any
    }
}

interface GroupedPlan {
    name: string
    description: string
    monthlyPrice: number
    yearlyPrice: number
    monthlyPlanId: string
    yearlyPlanId: string
    isEnterprise?: boolean
    features: string[]
    monthlyCredits?: string
    maxBrands?: string
    structuredFeatures?: any
}

interface PricingPlansDisplayProps {
    plans: Plan[]
    billingInterval: 'monthly' | 'yearly'
    onBillingIntervalChange: (interval: 'monthly' | 'yearly') => void
    onPlanSelect: (planId: string, planName: string, planPrice: number, isEnterprise?: boolean) => void
    currentPlanId?: string
    isChangingPlan?: boolean
    loading?: boolean
    showComparisonTable?: boolean
    variant?: 'public' | 'settings'
    className?: string
}

// Helper functions for structured features parsing
const parseStructuredFeatures = (structuredFeatures: any): {
    monthlyCredits: string
    maxBrands: string
    features: any
} => {
    if (!structuredFeatures) return {
        monthlyCredits: '',
        maxBrands: '',
        features: {}
    }
    
    const parsedFeatures = {
        monthlyCredits: structuredFeatures.monthly_credits || '',
        maxBrands: structuredFeatures.max_brands || '',
        features: {}
    }
    
    // Parse the features JSON string if it exists
    if (structuredFeatures.features) {
        try {
            parsedFeatures.features = typeof structuredFeatures.features === 'string' 
                ? JSON.parse(structuredFeatures.features)
                : structuredFeatures.features
        } catch (e) {
            console.warn('Failed to parse features JSON:', e)
        }
    }
    
    return parsedFeatures
}

const formatCredits = (creditsStr: string): string => {
    if (!creditsStr) return ''
    // Handle already formatted numbers (with commas) or plain numbers
    const cleanNumber = creditsStr.replace(/,/g, '')
    const num = parseInt(cleanNumber, 10)
    if (isNaN(num)) return creditsStr
    return num.toLocaleString()
}

const formatBrandLimit = (brandsStr: string): string => {   
    if (!brandsStr) return ''
    const num = parseInt(brandsStr, 10)
    if (isNaN(num)) return brandsStr
    if (num === 999999 || num >= 999999) return 'Unlimited'
    return num.toString()
}

// Helper function to get plan visual attributes based on index or name
const getPlanAttributes = (plan: GroupedPlan, index: number, variant: 'public' | 'settings') => {
    const icons = [Users, MessageSquare, Star, BarChart3, Crown]
    const gradients = [
        'from-blue-500 to-cyan-600',
        'from-indigo-500 to-purple-600', 
        'from-purple-600 to-pink-600',
        'from-pink-600 to-rose-600',
        'from-rose-600 to-orange-600'
    ]
    
    // Use index to determine visual attributes, with fallbacks
    const iconIndex = Math.min(index, icons.length - 1)
    const gradientIndex = Math.min(index, gradients.length - 1)
    
    // Mark Professional Plan as popular specifically
    const isPopular = plan.name.toLowerCase() === 'professional'
    
    // Popular plan always gets emphasized button, others get secondary or outline
    const buttonVariant: 'default' | 'secondary' | 'outline' = isPopular ? 'default' : 
        (plan.isEnterprise ? 'outline' : 'secondary')
    
    return {
        icon: icons[iconIndex],
        gradient: gradients[gradientIndex],
        buttonVariant,
        isPopular: variant === 'public' ? isPopular : false // Only show popular in public variant
    }
}

// Group plans by their base name (e.g., "basic", "professional", "enterprise")
const groupPlans = (plans: Plan[] = []) => {
    const grouped: { [key: string]: GroupedPlan } = {}

    plans.forEach(plan => {
        const baseName = plan.product_name.toLowerCase().split('_')[0]
        const isYearly = plan.interval === 'year'
        const parsedFeatures = parseStructuredFeatures(plan.structured_features)

        if (!grouped[baseName]) {
            grouped[baseName] = {
                name: baseName.charAt(0).toUpperCase() + baseName.slice(1),
                description: plan.product_description || `${baseName.charAt(0).toUpperCase() + baseName.slice(1)} plan with essential features`,
                monthlyPrice: 0,
                yearlyPrice: 0,
                monthlyPlanId: '',
                yearlyPlanId: '',
                isEnterprise: baseName === 'enterprise',
                features: (plan.features || []).map(feature => 
                    typeof feature === 'object' ? feature.name : String(feature)
                ),
                monthlyCredits: parsedFeatures.monthlyCredits,
                maxBrands: parsedFeatures.maxBrands,
                structuredFeatures: parsedFeatures.features
            }
        }

        if (isYearly) {
            grouped[baseName].yearlyPrice = plan.price / 100 / 12 // Convert to monthly equivalent
            grouped[baseName].yearlyPlanId = plan.id
        } else {
            grouped[baseName].monthlyPrice = plan.price / 100
            grouped[baseName].monthlyPlanId = plan.id
        }
    })

    // Add Enterprise plan if it doesn't exist
    if (!grouped['enterprise']) {
        grouped['enterprise'] = {
            name: 'Enterprise',
            description: 'Custom enterprise solution with unlimited features and dedicated support',
            monthlyPrice: 0, // Custom pricing
            yearlyPrice: 0, // Custom pricing
            monthlyPlanId: 'enterprise',
            yearlyPlanId: 'enterprise',
            isEnterprise: true,
            features: [
                'Unlimited monthly credits',
                'Unlimited brands',
                'Advanced team collaboration',
                'Priority support',
                'Custom integrations',
                'Dedicated account manager',
                'SLA guarantees',
                'Advanced analytics & reporting',
                'Custom training & onboarding',
                'White-label options'
            ],
            monthlyCredits: 'Unlimited',
            maxBrands: 'Unlimited',
            structuredFeatures: { teams: true }
        }
    }

    return Object.values(grouped).sort((a, b) => {
        // Sort by price, but put Enterprise last
        if (a.isEnterprise) return 1
        if (b.isEnterprise) return -1
        return a.monthlyPrice - b.monthlyPrice
    })
}

export default function PricingPlansDisplay({
    plans,
    billingInterval,
    onBillingIntervalChange,
    onPlanSelect,
    currentPlanId,
    isChangingPlan: _isChangingPlan = false,
    loading = false,
    showComparisonTable = true,
    variant = 'public',
    className = ''
}: PricingPlansDisplayProps) {
    const groupedPlans = useMemo(() => {
        return groupPlans(plans)
    }, [plans])
    
    // Transform grouped plans into display format with visual attributes
    const displayPlans = useMemo(() => {
        return groupedPlans.map((plan, index) => {
            const attributes = getPlanAttributes(plan, index, variant)
            const currentPrice = billingInterval === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice
            const savings = billingInterval === 'yearly' && plan.monthlyPrice > 0 
                ? Math.round(((plan.monthlyPrice - plan.yearlyPrice) / plan.monthlyPrice) * 100) 
                : 0
            
            return {
                id: billingInterval === 'monthly' ? plan.monthlyPlanId : plan.yearlyPlanId,
                name: plan.name,
                description: plan.description,
                currentPrice,
                originalMonthlyPrice: plan.monthlyPrice,
                savings,
                isEnterprise: plan.isEnterprise || plan.monthlyPrice === 0,
                features: plan.features,
                monthlyPlanId: plan.monthlyPlanId,
                yearlyPlanId: plan.yearlyPlanId,
                monthlyCredits: plan.monthlyCredits,
                maxBrands: plan.maxBrands,
                structuredFeatures: plan.structuredFeatures,
                ...attributes
            }
        })
    }, [groupedPlans, billingInterval, variant])

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        )
    }

    // Always use public styling for consistent visual appearance
    const isPublicVariant = true // Always use the better-looking public styling
    const functionalVariant = variant // Keep the functional variant for behavior differences

    return (
        <div className={`space-y-8 ${className}`}>
            {/* Billing Interval Toggle */}
            <div className="text-center">
                <div className={`${
                    isPublicVariant 
                        ? 'relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg p-2 inline-flex items-center justify-center'
                        : 'inline-flex items-center justify-center rounded-lg bg-muted p-1'
                }`}>
                    {isPublicVariant && (
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-purple-50/30" />
                    )}
                    <Button
                        variant={billingInterval === 'monthly' ? 'default' : 'ghost'}
                        onClick={() => onBillingIntervalChange('monthly')}
                        className={isPublicVariant ? `relative px-8 py-3 font-medium transition-all duration-300 ${
                            billingInterval === 'monthly' 
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:shadow-lg' 
                                : 'bg-transparent text-gray-600 hover:text-blue-600 hover:bg-blue-50/50'
                        }` : 'relative px-6'}
                    >
                        {isPublicVariant ? 'Pay monthly' : 'Monthly'}
                    </Button>
                    <Button
                        variant={billingInterval === 'yearly' ? 'default' : 'ghost'}
                        onClick={() => onBillingIntervalChange('yearly')}
                        className={isPublicVariant ? `relative px-8 py-3 font-medium transition-all duration-300 ${
                            billingInterval === 'yearly' 
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:shadow-lg' 
                                : 'bg-transparent text-gray-600 hover:text-blue-600 hover:bg-blue-50/50'
                        }` : 'relative px-6'}
                    >
                        {isPublicVariant ? 'Pay annually' : 'Yearly'}
                        <Badge className={`ml-2 ${
                            isPublicVariant 
                                ? 'bg-green-100 text-green-700 text-xs px-2 py-0.5 font-semibold border-0'
                                : 'absolute -top-3 -right-3 flex h-6 items-center rounded-full bg-primary px-2 py-1 text-[10px] font-medium text-primary-foreground'
                        }`}>
                            Save 20%
                        </Badge>
                    </Button>
                </div>
            </div>

            {/* Pricing Cards */}
            {groupedPlans.length === 0 ? (
                <div className="text-center py-16">
                    <div className="text-gray-500 mb-4">No pricing plans available at the moment.</div>
                    <div className="text-sm text-gray-400">Please check back later or contact support.</div>
                </div>
            ) : (
                <div className={`grid gap-6 items-stretch ${
                    isPublicVariant ? 'lg:gap-8' : 'gap-8'
                } ${
                    displayPlans.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' :
                    displayPlans.length === 2 ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto' :
                    displayPlans.length === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto' :
                    displayPlans.length === 4 ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4 max-w-7xl mx-auto' :
                    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto'
                }`}>
                    {displayPlans.map((plan) => {
                        const IconComponent = plan.icon
                        const isCurrentPlan = plan.id === currentPlanId
                        
                        return (
                            <Card 
                                key={plan.id} 
                                className={`group relative flex flex-col h-full overflow-hidden transition-all duration-500 ${
                                    isPublicVariant 
                                        ? `hover:shadow-2xl border-2 ${
                                            plan.isPopular 
                                                ? 'border-purple-400 shadow-2xl ring-4 ring-purple-100/50 bg-gradient-to-br from-white via-purple-50/60 to-indigo-50/40 transform scale-[1.05] hover:scale-[1.06]' 
                                                : 'border-gray-200/80 shadow-lg hover:shadow-xl bg-gradient-to-br from-white via-slate-50/40 to-gray-50/30 hover:border-gray-300/80 hover:scale-[1.02]'
                                        }`
                                        : `${plan.name === 'Professional' ? 'border-primary shadow-lg scale-[1.02] md:scale-105' : ''} ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`
                                }`}
                            >
                                {/* Popular badge removed from top - now shown inline */}
                                
                                {isPublicVariant && (
                                    <>
                                        {/* Enhanced floating gradient circles */}
                                        <div className={`absolute top-0 right-0 w-28 h-28 bg-gradient-to-br ${plan.gradient} ${
                                            plan.isPopular ? 'opacity-20' : 'opacity-8'
                                        } rounded-full -translate-y-14 translate-x-14 animate-pulse`} />
                                        <div className={`absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr ${plan.gradient} ${
                                            plan.isPopular ? 'opacity-15' : 'opacity-6'
                                        } rounded-full translate-y-10 -translate-x-10`} />
                                        
                                        {/* Popular plan accent border */}
                                        {plan.isPopular && (
                                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-transparent to-indigo-500/20 rounded-lg pointer-events-none" />
                                        )}
                                    </>
                                )}
                                
                                <CardHeader className={`relative flex-grow space-y-4 ${isPublicVariant ? 'pt-8 pb-4' : ''}`}>
                                    <div className={`${isPublicVariant ? 'text-center ' : ''}space-y-3`}>
                                        {isPublicVariant && (
                                            <div className={`inline-flex p-3 rounded-2xl bg-gradient-to-br ${plan.gradient} shadow-lg group-hover:shadow-xl transition-all duration-300 ${
                                                plan.isPopular ? 'ring-2 ring-purple-200 ring-offset-2' : ''
                                            }`}>
                                                <IconComponent className={`${plan.isPopular ? 'h-7 w-7' : 'h-6 w-6'} text-white`} />
                                            </div>
                                        )}
                                        
                                        <div className={`space-y-1.5 ${isPublicVariant ? '' : 'space-y-2'}`}>
                                            <h3 className={`${
                                                isPublicVariant 
                                                    ? `${plan.isPopular ? 'text-2xl' : 'text-xl'} font-bold ${
                                                        plan.isPopular ? 'bg-gradient-to-r from-purple-700 to-indigo-700 bg-clip-text text-transparent' : 'text-gray-900'
                                                    } group-hover:text-gray-800 transition-colors`
                                                    : 'text-2xl font-bold'
                                            }`}>{plan.name}</h3>
                                            <p className={`${
                                                isPublicVariant 
                                                    ? 'text-xs text-gray-600 leading-relaxed px-2 max-w-xs mx-auto'
                                                    : 'text-sm text-muted-foreground'
                                            }`}>{plan.description}</p>
                                        </div>
                                        
                                        {/* Popular Badge - Only for public functional variant */}
                                        {functionalVariant === 'public' && plan.isPopular && (
                                            <div className="flex justify-center">
                                                <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1.5 text-xs font-bold border-0 shadow-lg rounded-full whitespace-nowrap">
                                                    <Star className="h-3 w-3 mr-1.5 fill-current" />
                                                    Most Popular
                                                </Badge>
                                            </div>
                                        )}
                                        
                                        <div className={`space-y-1.5 ${isPublicVariant ? '' : 'flex items-baseline'}`}>
                                            <div className={`${isPublicVariant ? 'flex items-baseline justify-center' : ''}`}>
                                                {plan.isEnterprise ? (
                                                    <span className={`${
                                                        isPublicVariant ? 'text-3xl font-bold text-gray-900' : 'text-4xl font-bold tracking-tight'
                                                    }`}>Custom</span>
                                                ) : (
                                                    <>
                                                        <span className={`${
                                                            isPublicVariant 
                                                                ? `${plan.isPopular ? 'text-4xl bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent' : 'text-3xl text-gray-900'} font-bold`
                                                                : 'text-4xl font-bold tracking-tight'
                                                        }`}>
                                                            ${plan.currentPrice.toFixed(0)}
                                                        </span>
                                                        <span className={`${
                                                            isPublicVariant ? 'text-sm text-gray-600 ml-1' : 'text-sm font-normal text-muted-foreground ml-1'
                                                        }`}>/month</span>
                                                    </>
                                                )}
                                            </div>
                                            
                                            {billingInterval === 'yearly' && !plan.isEnterprise && plan.savings > 0 && (
                                                <div className={`text-xs font-medium ${
                                                    plan.isPopular ? 'text-green-700' : 'text-green-600'
                                                }`}>
                                                    Save {plan.savings}% annually
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Key differentiators - Only for public variant */}
                                        {isPublicVariant && (
                                            <div className={`space-y-2 pt-3 ${
                                                plan.isPopular 
                                                    ? 'border-t-2 border-gradient-to-r from-purple-200 to-indigo-200' 
                                                    : 'border-t border-gray-200/60'
                                            }`}>
                                                {plan.monthlyCredits && (
                                                    <div className="flex items-center justify-center space-x-2 text-xs font-semibold">
                                                        <div className={`p-1.5 rounded-lg bg-gradient-to-r ${plan.gradient} shadow-sm`}>
                                                            <CreditCard className="h-3 w-3 text-white" />
                                                        </div>
                                                        <span className={plan.isPopular ? 'text-purple-800' : 'text-gray-800'}>
                                                            {formatCredits(plan.monthlyCredits)} credits/month
                                                        </span>
                                                    </div>
                                                )}
                                                {plan.maxBrands && (
                                                    <div className="flex items-center justify-center space-x-2 text-xs font-semibold">
                                                        <div className={`p-1.5 rounded-lg bg-gradient-to-r ${plan.gradient} shadow-sm`}>
                                                            <Building2 className="h-3 w-3 text-white" />
                                                        </div>
                                                        <span className={plan.isPopular ? 'text-purple-800' : 'text-gray-800'}>
                                                            {formatBrandLimit(plan.maxBrands)} brands
                                                        </span>
                                                    </div>
                                                )}
                                                {plan.structuredFeatures?.teams && (
                                                    <div className="flex items-center justify-center space-x-2 text-xs font-semibold">
                                                        <div className={`p-1.5 rounded-lg bg-gradient-to-r ${plan.gradient} shadow-sm`}>
                                                            <Users className="h-3 w-3 text-white" />
                                                        </div>
                                                        <span className={plan.isPopular ? 'text-purple-800' : 'text-gray-800'}>Team Collaboration</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </CardHeader>
                                
                                <CardContent className="relative flex flex-col pt-0 pb-6 mt-auto">
                                    {functionalVariant === 'settings' && (
                                        <Button
                                            className={`w-full py-3.5 text-sm font-bold transition-all duration-300 hover:transform hover:scale-[1.02] mb-4 ${
                                                plan.isEnterprise
                                                    ? 'bg-gray-900 text-white hover:bg-gray-800'
                                                    : plan.isPopular
                                                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg hover:shadow-xl hover:from-purple-700 hover:to-indigo-700'
                                                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                                            } ${
                                                isCurrentPlan ? 'opacity-75 cursor-not-allowed' : ''
                                            }`}
                                            disabled={isCurrentPlan}
                                            onClick={() => {
                                                const planId = billingInterval === 'monthly' ? plan.monthlyPlanId : plan.yearlyPlanId
                                                onPlanSelect(planId || plan.id, plan.name, plan.currentPrice, plan.isEnterprise)
                                            }}
                                        >
                                            {isCurrentPlan ? 'Current Plan' : (plan.isEnterprise ? 'Contact Sales' : 'Select Plan')}
                                        </Button>
                                    )}

                                    <div className={`space-y-3 ${isPublicVariant ? 'flex-grow' : ''}`}>
                                        <div className={`text-xs font-semibold ${
                                            isPublicVariant 
                                                ? `${plan.isPopular ? 'text-purple-900' : 'text-gray-900'} border-b ${plan.isPopular ? 'border-purple-200' : 'border-gray-200'} pb-2`
                                                : 'text-sm font-medium'
                                        }`}>What's included:</div>
                                        <ul className={`space-y-2 ${isPublicVariant ? 'text-xs' : 'space-y-3 text-sm'}`}>
                                            {plan.features.map((feature, i) => (
                                                <li key={i} className="flex gap-2.5">
                                                    <Check className={`${
                                                        isPublicVariant 
                                                            ? `h-3.5 w-3.5 flex-shrink-0 ${plan.isPopular ? 'text-green-700' : 'text-green-600'} mt-0.5`
                                                            : 'h-5 w-5 flex-shrink-0 text-primary'
                                                    }`} />
                                                    <span className={`${
                                                        isPublicVariant 
                                                            ? `${plan.isPopular ? 'text-gray-800' : 'text-gray-700'} leading-tight font-medium`
                                                            : ''
                                                    }`}>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    
                                    {functionalVariant === 'public' && (
                                        <Button
                                            className={`w-full py-3.5 text-sm font-bold transition-all duration-300 hover:transform hover:scale-[1.02] mt-4 ${
                                                plan.isPopular
                                                    ? `bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 text-white shadow-2xl hover:shadow-3xl border-0 ring-2 ring-purple-300 hover:ring-purple-400 hover:from-purple-700 hover:via-indigo-700 hover:to-purple-800`
                                                    : plan.isEnterprise 
                                                    ? 'bg-gradient-to-r from-gray-700 to-gray-900 text-white shadow-lg hover:shadow-xl border-0 hover:from-gray-800 hover:to-gray-900'
                                                    : plan.buttonVariant === 'outline'
                                                    ? `border-2 border-gray-300 bg-transparent text-gray-700 hover:bg-gradient-to-r ${plan.gradient} hover:text-white hover:border-transparent hover:shadow-lg`
                                                    : 'bg-white border-2 border-gray-200 text-gray-900 hover:border-gray-400 hover:shadow-lg hover:bg-gray-50'
                                            }`}
                                            onClick={() => {
                                                const planId = billingInterval === 'monthly' ? plan.monthlyPlanId : plan.yearlyPlanId
                                                onPlanSelect(planId || plan.id, plan.name, plan.currentPrice, plan.isEnterprise)
                                            }}
                                        >
                                            {plan.isEnterprise ? (
                                                <>
                                                    <Shield className="h-4 w-4 mr-2" />
                                                    Contact Sales
                                                </>
                                            ) : (
                                                <>
                                                    <Zap className="h-4 w-4 mr-2" />
                                                    Start Free Trial
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        )
                    })}
                </div>
            )}
            
            {/* Comparison Table - Only show if enabled and we have plans */}
            {showComparisonTable && displayPlans.length > 0 && (
                <div className="relative">
                    <div className={`relative overflow-hidden ${
                        isPublicVariant 
                            ? 'bg-gradient-to-br from-white via-indigo-50/20 to-purple-50/30 rounded-2xl border border-indigo-200/50 shadow-lg'
                            : 'bg-white rounded-lg border shadow-sm'
                    }`}>
                        {isPublicVariant && <div className="absolute inset-0 bg-grid-pattern opacity-5" />}
                        <div className={`relative ${isPublicVariant ? 'backdrop-blur-sm p-4 sm:p-6 lg:p-8' : 'p-6'}`}>
                            <div className={`text-center ${isPublicVariant ? 'mb-8 sm:mb-12' : 'mb-6'}`}>
                                <h2 className={`${
                                    isPublicVariant 
                                        ? 'text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-700 via-purple-700 to-blue-700 bg-clip-text text-transparent mb-4'
                                        : 'text-xl font-semibold text-gray-900 mb-2'
                                }`}>
                                    Detailed Comparison
                                </h2>
                                <p className={`text-gray-600 ${isPublicVariant ? 'text-base sm:text-lg' : 'text-sm'}`}>
                                    Everything you need to know about our pricing plans
                                </p>
                            </div>
                            
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className={`text-left font-semibold text-gray-900 ${
                                                isPublicVariant ? 'py-3 sm:py-4 px-3 sm:px-6' : 'py-3 px-4'
                                            }`}>Features</th>
                                            {displayPlans.map((plan) => (
                                                <th key={plan.id} className={`text-center font-semibold text-gray-900 ${
                                                    isPublicVariant 
                                                        ? 'py-3 sm:py-4 px-2 sm:px-4 min-w-[100px] sm:min-w-[120px]'
                                                        : 'py-3 px-2 min-w-[100px]'
                                                }`}>
                                                    <div className="truncate">{plan.name}</div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        <tr className="hover:bg-blue-50/30 transition-colors">
                                            <td className={`font-medium text-gray-900 ${
                                                isPublicVariant ? 'py-3 sm:py-4 px-3 sm:px-6 text-sm sm:text-base' : 'py-3 px-4 text-sm'
                                            }`}>Monthly Price</td>
                                            {displayPlans.map((plan) => (
                                                <td key={plan.id} className={`text-center text-gray-700 ${
                                                    isPublicVariant ? 'py-3 sm:py-4 px-2 sm:px-4' : 'py-3 px-2'
                                                }`}>
                                                    <div className="font-semibold">
                                                        {plan.isEnterprise ? 'Custom' : `$${plan.originalMonthlyPrice.toFixed(0)}`}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                        {billingInterval === 'yearly' && (
                                            <tr className="hover:bg-blue-50/30 transition-colors">
                                                <td className={`font-medium text-gray-900 ${
                                                    isPublicVariant ? 'py-4 px-6' : 'py-3 px-4'
                                                }`}>Annual Price (per month)</td>
                                                {displayPlans.map((plan) => (
                                                    <td key={plan.id} className={`text-center text-gray-700 ${
                                                        isPublicVariant ? 'py-4 px-4' : 'py-3 px-2'
                                                    }`}>
                                                        <div className="font-semibold">
                                                            {plan.isEnterprise ? 'Custom' : `$${plan.currentPrice.toFixed(0)}`}
                                                        </div>
                                                        {plan.savings > 0 && (
                                                            <div className="text-xs text-green-600 mt-1">Save {plan.savings}%</div>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        )}
                                        {/* Monthly Credits Row */}
                                        {displayPlans.some(plan => plan.monthlyCredits) && (
                                            <tr className="hover:bg-blue-50/30 transition-colors">
                                                <td className={`font-medium text-gray-900 ${
                                                    isPublicVariant ? 'py-4 px-6' : 'py-3 px-4'
                                                }`}>
                                                    <div className="flex items-center space-x-2">
                                                        <CreditCard className="h-4 w-4 text-indigo-600" />
                                                        <span>Monthly Credits</span>
                                                    </div>
                                                </td>
                                                {displayPlans.map((plan) => (
                                                    <td key={plan.id} className={`text-center text-gray-700 ${
                                                        isPublicVariant ? 'py-4 px-4' : 'py-3 px-2'
                                                    }`}>
                                                        <div className="font-semibold text-indigo-600">
                                                            {plan.monthlyCredits ? formatCredits(plan.monthlyCredits) : plan.isEnterprise ? 'Custom' : 'N/A'}
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        )}
                                        {/* Brand Limit Row */}
                                        {displayPlans.some(plan => plan.maxBrands) && (
                                            <tr className="hover:bg-blue-50/30 transition-colors">
                                                <td className={`font-medium text-gray-900 ${
                                                    isPublicVariant ? 'py-4 px-6' : 'py-3 px-4'
                                                }`}>
                                                    <div className="flex items-center space-x-2">
                                                        <Building2 className="h-4 w-4 text-purple-600" />
                                                        <span>Number of Brands</span>
                                                    </div>
                                                </td>
                                                {displayPlans.map((plan) => (
                                                    <td key={plan.id} className={`text-center text-gray-700 ${
                                                        isPublicVariant ? 'py-4 px-4' : 'py-3 px-2'
                                                    }`}>
                                                        <div className="font-semibold text-purple-600">
                                                            {plan.maxBrands ? formatBrandLimit(plan.maxBrands) : plan.isEnterprise ? 'Unlimited' : 'N/A'}
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        )}
                                        {/* Team Collaboration Row */}
                                        {displayPlans.some(plan => plan.structuredFeatures?.teams !== undefined) && (
                                            <tr className="hover:bg-blue-50/30 transition-colors">
                                                <td className={`font-medium text-gray-900 ${
                                                    isPublicVariant ? 'py-4 px-6' : 'py-3 px-4'
                                                }`}>
                                                    <div className="flex items-center space-x-2">
                                                        <Users className="h-4 w-4 text-green-600" />
                                                        <span>Team Collaboration</span>
                                                    </div>
                                                </td>
                                                {displayPlans.map((plan) => (
                                                    <td key={plan.id} className={`text-center text-gray-700 ${
                                                        isPublicVariant ? 'py-4 px-4' : 'py-3 px-2'
                                                    }`}>
                                                        {plan.structuredFeatures?.teams !== undefined ? (
                                                            plan.structuredFeatures.teams || plan.isEnterprise ? (
                                                                <Check className="h-5 w-5 text-green-600 mx-auto" />
                                                            ) : (
                                                                <span className="text-gray-400">—</span>
                                                            )
                                                        ) : plan.isEnterprise ? (
                                                            <Check className="h-5 w-5 text-green-600 mx-auto" />
                                                        ) : (
                                                            <span className="text-gray-400">—</span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        )}
                                        {/* Show features comparison if available */}
                                        {displayPlans.some(plan => plan.features.length > 0) && (
                                            <tr className="hover:bg-blue-50/30 transition-colors">
                                                <td className={`font-medium text-gray-900 ${
                                                    isPublicVariant ? 'py-4 px-6' : 'py-3 px-4'
                                                }`}>Features</td>
                                                {displayPlans.map((plan) => (
                                                    <td key={plan.id} className={`text-center text-gray-700 ${
                                                        isPublicVariant ? 'py-4 px-4' : 'py-3 px-2'
                                                    }`}>
                                                        <div className="text-sm space-y-1">
                                                            {plan.features.slice(0, 3).map((feature, i) => (
                                                                <div key={i} className="flex items-center justify-center">
                                                                    <Check className="h-3 w-3 text-green-600 mr-1 flex-shrink-0" />
                                                                    <span className="text-xs truncate">{feature}</span>
                                                                </div>
                                                            ))}
                                                            {plan.features.length > 3 && (
                                                                <div className="text-xs text-gray-500">
                                                                    +{plan.features.length - 3} more
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}